<template>
  <div>
    <h3>Metas e conquistas</h3>

    <b-row v-if="loading" class="d-flex justify-content-between mt-2">
      <b-col v-for="n in 7" :key="n" class="d-flex justify-content-center mb-1" style="flex: 1; padding: 0 5px;">
        <div class="medal-card-skeleton" />
      </b-col>
    </b-row>

    <b-row v-else class="d-flex justify-content-between mt-2">
      <b-col
        v-for="badge in enrichedBadges"
        :key="badge.key"
        class="d-flex justify-content-center mb-2"
        style="flex: 1; padding: 0 5px;"
      >
        <MedalCard
          :title="badge.title"
          :image="badge.image"
          :level-name="badge.levelName"
          :current-count="badge.currentCount"
          :next-threshold="badge.nextThreshold"
          :next-level-name="badge.nextLevel"
          :progress-pct="badge.progressPct"
          :achieved-at="badge.achievedAt"
          @click="showModal(badge.modalId)"
        />
      </b-col>

      <!-- Nível PN -->
      <b-col class="d-flex justify-content-center mb-2" style="flex: 1; padding: 0 5px;">
        <MedalCard
          title="Nível PN"
          :image="roleBadgeImage"
          :level-name="roleBadges && roleBadges.name"
          :current-count="roleBadges && roleBadges.value ? 1 : 0"
          :next-threshold="null"
          :progress-pct="0"
          :achieved-at="null"
          :career-path="roleCareerPath"
          @click="showModal(1)"
        />
      </b-col>
    </b-row>

    <BadgesModal v-model="showModalFlag" :badge-id="selectedBadgeId" :badge-data="selectedBadgeData" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as types from '../store/types'
import * as accountTypes from '@/modules/account/store/types'
import MedalCard from './MedalCard.vue'
import BadgesModal from './BadgesModal.vue'
import { isPnRole } from '@/constants/auth'

// Common path before the branch, then two lanes after FA
const CAREER_COMMON   = ['PNH', 'PNC', 'FA']
const CAREER_LANE_A   = ['FE', 'FR', 'FN', 'FF']   // consultancy lane
const CAREER_LANE_B   = ['FO', 'FP', 'FC']          // franchise lane

const BADGE_STATIC = [
  { key: 'appointment',   title: 'Agendamento',      modalId: 2 },
  { key: 'subscription',  title: 'Assinaturas',       modalId: 3 },
  { key: 'ep',            title: 'EPs',               modalId: 4 },
  { key: 'lifetotals',    title: 'Gestão de risco',   modalId: 5 },
  { key: 'consortium',    title: 'Aquisição de bens', modalId: 6 },
  { key: 'totalearnings', title: 'Faturamento',       modalId: 7 },
]

const LOCKED_IMAGES = {
  appointment:   'https://futurogfp-assets.s3.sa-east-1.amazonaws.com/Medalhas-2/ENTREVISTA+AGENDADA/Bronze-EA+locked.png',
  subscription:  'https://futurogfp-assets.s3.sa-east-1.amazonaws.com/Medalhas-2/ASSINATURAS/Bronze-ASS+locked.png',
  ep:            'https://futurogfp-assets.s3.sa-east-1.amazonaws.com/Medalhas-2/ENTREVISTA+PAGA/Bronze-EP+locked.png',
  lifetotals:    'https://futurogfp-assets.s3.sa-east-1.amazonaws.com/Medalhas-2/VIDA/Bronze-VIDA+locked.png',
  consortium:    'https://futurogfp-assets.s3.sa-east-1.amazonaws.com/Medalhas-2/AQUISI%C3%87%C3%83O+DE+BENS/Bronze-AB+locked.png',
  totalearnings: 'https://futurogfp-assets.s3.sa-east-1.amazonaws.com/Medalhas-2/FATURAMENTO/Bronze-FATURAMENTO+locked000.png',
}

export default {
  name: 'BadgesDashboard',
  components: { MedalCard, BadgesModal },
  data() {
    return {
      showModalFlag:    false,
      selectedBadgeId:  null,
      selectedBadgeData: null,
      loading:          false,
    }
  },
  computed: {
    ...mapGetters({
      dashboard: types.BADGES_DASHBOARD,
      roleBadges: types.ROLE_BADGES,
      user: accountTypes.USER,
    }),
    roleBadgeImage() {
      return (this.roleBadges && this.roleBadges.imageActive) || 'https://futurogfp-assets.s3.sa-east-1.amazonaws.com/medalhas/Cargos/PNC.png'
    },
    roleCareerPath() {
      const name = this.roleBadges && this.roleBadges.name
      if (!name) return null
      // On common segment — branches flag only set at FA (last common step, index 2)
      const ci = CAREER_COMMON.indexOf(name)
      if (ci !== -1) {
        return { track: CAREER_COMMON, currentIndex: ci, branches: ci === CAREER_COMMON.length - 1 }
      }
      // On lane A — show full path (common already past)
      const ai = CAREER_LANE_A.indexOf(name)
      if (ai !== -1) {
        return { track: [...CAREER_COMMON, ...CAREER_LANE_A], currentIndex: CAREER_COMMON.length + ai }
      }
      // On lane B — show full path (common already past)
      const bi = CAREER_LANE_B.indexOf(name)
      if (bi !== -1) {
        return { track: [...CAREER_COMMON, ...CAREER_LANE_B], currentIndex: CAREER_COMMON.length + bi }
      }
      return null
    },
    isPn() {
      return isPnRole(this.user?.user_contract_type_id)
    },
    enrichedBadges() {
      if (!this.dashboard) return []
      return BADGE_STATIC.map((static_) => {
        const data = this.dashboard[static_.key] || {}
        const earned = (data.value || 0) > 0
        return {
          ...static_,
          image:        earned ? (data.imageActive || LOCKED_IMAGES[static_.key]) : (data.imageInactive || LOCKED_IMAGES[static_.key]),
          levelName:    (data.value || 0) > 0 ? data.name : null,
          currentCount: data.current_count || 0,
          nextThreshold:data.next_threshold,
          nextLevel:    data.next_level,
          progressPct:  data.progress_pct || 0,
          achievedAt:   data.achieved_at,
          levels:       data.levels || [],
        }
      })
    },
  },
  methods: {
    ...mapActions({
      getBadgesDashboard: types.GET_BADGES_DASHBOARD,
      getRoleBadges:      types.GET_ROLE_BADGES,
    }),
    async loadAll() {
      this.loading = true
      try {
        await Promise.all([
          this.getBadgesDashboard(),
          this.getRoleBadges(),
        ])
      } catch {
        // silent — individual cards show locked state on missing data
      } finally {
        this.loading = false
      }
    },
    showModal(badgeId) {
      this.selectedBadgeId = badgeId
      // Pass levels data to modal so it can build the timeline without extra requests
      const keyMap = { 2: 'appointment', 3: 'subscription', 4: 'ep', 5: 'lifetotals', 6: 'consortium', 7: 'totalearnings' }
      this.selectedBadgeData = this.dashboard?.[keyMap[badgeId]] || null
      this.showModalFlag = true
    },
  },
  mounted() {
    if (this.isPn) {
      this.$nextTick(() => this.loadAll())
    }
  },
}
</script>

<style scoped>
.medal-card-skeleton {
  width: 100%;
  min-width: 110px;
  height: 210px;
  border-radius: 14px;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: skeletonShimmer 1.5s ease-in-out infinite;
}

.dark-layout .medal-card-skeleton {
  background: linear-gradient(90deg, #1a2035 25%, #232b45 50%, #1a2035 75%);
  background-size: 200% 100%;
}

@keyframes skeletonShimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
